import type {
    CreateCustomFieldVariables,
    EntityType,
    FieldCreateInput,
} from 'nutshell-graphql-types';
import {FormFieldType} from 'nutshell-graphql-types';

import type {FormSchemaProperty} from '../../../../../core/nutshell-fields/types';
import {
    convertGraphQLResponseToFormFieldState,
    getFieldTypeFromDisplayType,
    translateGraphQLEntityTypeToSchema,
    translateSchemaEntityTypeToGraphQL,
} from '../../../../helpers';

import type {PageFieldArrayOps} from '../../drag-and-drop/types';
import type {DisplayType, Field, InputFormField} from '../../types';

import type {CustomFieldView} from '../blocks-and-design/content-blocks/types';

import {VIEWS_WITHOUT_PLACEHOLDER} from './consts';
import type {
    EditCustomFieldSession,
    NewCustomFieldFormPayload,
    SelectedExistingField,
} from './types';

export function applyCustomSingleChoiceEnumFieldDisplayTypeOverride(
    fieldTypeValue: string,
    fieldSchema: FormSchemaProperty | undefined,
    fieldDisplayType: string
): string {
    if (
        !fieldSchema ||
        !('isCustomField' in fieldSchema) ||
        !fieldSchema.isCustomField ||
        (fieldSchema.displayType !== 'radio' && fieldSchema.displayType !== 'dropdown')
    ) {
        return fieldDisplayType;
    }
    if (fieldTypeValue === 'INPUT') {
        return 'dropdown';
    }
    if (fieldTypeValue === 'RADIO') {
        return 'RADIO';
    }

    return fieldDisplayType;
}

export function isFormFieldInputRow(field: Field): field is InputFormField {
    return 'entityType' in field;
}

export function displayTypeToCustomFieldView(displayType: DisplayType): CustomFieldView | null {
    switch (displayType) {
        case 'textfield':
            return 'short-text';
        case 'textarea':
            return 'long-text';
        case 'dropdown':
            return 'dropdown';
        case 'radio':
            return 'radio';
        case 'checkboxes':
            return 'checkbox';
        case 'date':
            return 'date';
        case 'singleLineAddress':
        case 'multiLineAddress':
        case 'file':
        case 'url':
        default:
            return null;
    }
}

export function resolveCustomFieldViewForDroppedExistingField(
    displayType: DisplayType,
    field: Field
): CustomFieldView | null {
    const fromSchema = displayTypeToCustomFieldView(displayType);
    if (fromSchema) {
        return fromSchema;
    }
    if (!isFormFieldInputRow(field)) {
        return null;
    }
    switch (field.type) {
        case 'RADIO':
            return 'radio';
        case 'CHECKBOXES':
            return 'checkbox';
        case 'DATE':
            return 'date';
        case 'INPUT':
        case 'FILE':
        case 'URL':
        case 'IMAGE':
            return 'short-text';
        default:
            return null;
    }
}

export function buildFieldCreateInputForExistingCustomSchemaField(
    field: FormSchemaProperty,
    formId: string
): FieldCreateInput {
    const fieldKey = field.attributeId ?? field.name;
    const type =
        field.displayType === 'date'
            ? FormFieldType.DATE
            : getFieldTypeFromDisplayType(field.displayType);
    return {
        type,
        formId,
        entityName: translateSchemaEntityTypeToGraphQL(field.entityType),
        entityAttributeId: fieldKey,
    };
}

export const ENTITY_NAME_TO_GRAPHQL_ENTITY_TYPE: Record<string, EntityType> = {
    Accounts: 'ACCOUNT',
    Contacts: 'CONTACT',
    Leads: 'LEAD',
};

export function customFieldAttributeTypeForCreate(view: CustomFieldView): string {
    switch (view) {
        case 'short-text':
            return 'STRING';
        case 'long-text':
            return 'TEXT';
        case 'dropdown':
        case 'radio':
        case 'checkbox':
            return 'ENUM';
        case 'date':
            return 'DATE';
    }
}

export function formFieldTypeForNewCustomView(view: CustomFieldView): FormFieldType {
    switch (view) {
        case 'radio':
            return FormFieldType.RADIO;
        case 'checkbox':
            return FormFieldType.CHECKBOXES;
        case 'date':
            return FormFieldType.DATE;
        default:
            return FormFieldType.INPUT;
    }
}

export function customFieldViewToSchemaDisplayType(view: CustomFieldView): DisplayType {
    switch (view) {
        case 'short-text':
            return 'textfield';
        case 'long-text':
            return 'textarea';
        case 'dropdown':
            return 'dropdown';
        case 'radio':
            return 'radio';
        case 'checkbox':
            return 'checkboxes';
        case 'date':
            return 'date';
    }
}

export function buildFieldCreateInputForNewCustomAttribute(args: {
    view: CustomFieldView;
    formId: string;
    entityType: EntityType;
    entityAttributeId: string;
}): FieldCreateInput {
    return {
        type: formFieldTypeForNewCustomView(args.view),
        formId: args.formId,
        entityName: args.entityType,
        entityAttributeId: args.entityAttributeId,
    };
}

export function buildCreateCustomFieldVariables(
    payload: NewCustomFieldFormPayload
): CreateCustomFieldVariables {
    const type = customFieldAttributeTypeForCreate(payload.view);

    const customField: {
        name: string;
        description: string;
        type: string;
        entityName: string;
        pipelines: null;
        choices?: string[];
        allowMultiple?: boolean;
    } = {
        name: payload.label,
        description: '',
        type,
        entityName: payload.entityName,
        pipelines: null,
    };

    if (type === 'ENUM') {
        customField.choices = payload.choices;
        customField.allowMultiple = Boolean(payload.view === 'checkbox');
    }

    return {customField};
}

type RhfSetValue = (
    name: string,
    value: unknown,
    options?: {shouldDirty?: boolean; shouldTouch?: boolean}
) => void;

export function applyNewCustomFieldFormRowValues(
    setValue: RhfSetValue,
    pageIndex: number,
    insertIndex: number,
    payload: NewCustomFieldFormPayload
): void {
    const pathBase = `pages.${pageIndex}.fields.${insertIndex}`;
    setValue(`${pathBase}.label`, payload.label, {shouldDirty: true, shouldTouch: true});
    if (!VIEWS_WITHOUT_PLACEHOLDER.has(payload.view)) {
        setValue(`${pathBase}.placeholder`, payload.placeholder, {
            shouldDirty: true,
            shouldTouch: true,
        });
    }
    setValue(`${pathBase}.isRequired`, payload.isRequired, {shouldDirty: true, shouldTouch: true});
}

export function formFieldRowToSelectedExistingField(field: Field): SelectedExistingField {
    if (!isFormFieldInputRow(field)) {
        throw new Error('formFieldRowToSelectedExistingField: expected an input row field');
    }
    return {
        id: field.attributeId ?? field.fieldName ?? '',
        label: field.label,
        entityType: translateGraphQLEntityTypeToSchema(field.entityType),
        choices: field.defaultValues ?? [],
    };
}

export type SubmitNewCustomFieldDeps = {
    payload: NewCustomFieldFormPayload;
    formId: string;
    pageIndex: number;
    insertIndex: number;
    ops: PageFieldArrayOps;
    createCustomField: (variables: CreateCustomFieldVariables) => Promise<any>;
    createField: (options: {variables: {input: FieldCreateInput}}) => Promise<any>;
    setValue: RhfSetValue;
    getValues: (name: string) => Field;
    refetchForm: () => Promise<any>;
    refreshFormSchemaInRedux: () => void;
};

export function submitNewCustomFieldToForm(
    deps: SubmitNewCustomFieldDeps
): Promise<EditCustomFieldSession | null> {
    const graphqlEntityType = ENTITY_NAME_TO_GRAPHQL_ENTITY_TYPE[deps.payload.entityName];
    if (!graphqlEntityType) {
        return Promise.reject(new Error('Select a valid entity.'));
    }

    const {
        payload,
        formId,
        pageIndex,
        insertIndex,
        ops,
        createCustomField,
        createField,
        setValue,
        getValues,
        refetchForm,
        refreshFormSchemaInRedux,
    } = deps;

    return createCustomField(buildCreateCustomFieldVariables(payload))
        .then((createResult) => {
            const customFieldId = createResult?.data?.createCustomField?.customField?.id;
            if (!customFieldId) {
                throw new Error('Create custom field failed.');
            }

            const input = buildFieldCreateInputForNewCustomAttribute({
                view: payload.view,
                formId,
                entityType: graphqlEntityType,
                entityAttributeId: customFieldId,
            });

            return createField({variables: {input}});
        })
        .then((fieldResult) => {
            const createdField = fieldResult?.data?.fieldCreate?.field;

            if (!createdField) {
                throw new Error('Add field to form failed.');
            }

            const converted = convertGraphQLResponseToFormFieldState(createdField);
            ops.insert(insertIndex, converted as Field);

            applyNewCustomFieldFormRowValues(setValue, pageIndex, insertIndex, payload);

            const rowAfter = getValues(
                `pages.${pageIndex}.fields.${insertIndex}` as never
            ) as Field;

            const view = resolveCustomFieldViewForDroppedExistingField(
                customFieldViewToSchemaDisplayType(payload.view),
                rowAfter
            );

            if (!view || !isFormFieldInputRow(rowAfter)) {
                return null;
            }

            return {
                view,
                existingField: formFieldRowToSelectedExistingField(rowAfter),
                editedFieldRef: {pageIndex, fieldIndex: insertIndex},
            };
        })
        .then((session) => {
            refreshFormSchemaInRedux();
            refetchForm();
            return session;
        });
}

export function getSearchPlaceholder(view: CustomFieldView): string {
    switch (view) {
        case 'long-text':
            return 'Search long text fields…';
        case 'dropdown':
        case 'radio':
            return 'Search decision fields…';
        case 'checkbox':
            return 'Search multiple choice decision fields…';
        case 'date':
            return 'Search date fields…';
        case 'short-text':
        default:
            return 'Search short text fields…';
    }
}

const EMPTY_LIST_PHRASE_BY_VIEW: Record<CustomFieldView, string> = {
    'short-text': 'short text',
    'long-text': 'long text',
    dropdown: 'single choice decision',
    radio: 'single choice decision',
    checkbox: 'multiple choice decision',
    date: 'date',
};

export function getCustomFieldListEmptyStateMessage(view: CustomFieldView): string {
    const phrase = EMPTY_LIST_PHRASE_BY_VIEW[view];
    return `You haven’t created any ${phrase} custom fields yet. Create a new custom field to get started.`;
}
